/**
 * `/security` — the second factor.
 *
 * Two things this screen has to get right, and both are about the moment things go wrong rather
 * than the happy path.
 *
 * **The recovery codes appear exactly once.** So they are not a footnote after a success message —
 * they are the whole screen at that moment, with the sentence that matters above them: nobody can
 * show them again, including us. A page that buried them under a green tick would produce a Site
 * Administrator locked out of their own platform six months later.
 *
 * **What MFA protects is stated plainly.** It guards site administration and nothing else. Somebody
 * who enrols expecting their own celebrations to be protected has been misled by the absence of a
 * sentence, and that is our fault rather than theirs.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import QRCode from 'qrcode';

import type { ApiError } from '../../api/client';
import { Notice } from '../../components/primitives';
import {
  beginEnrolment,
  confirmEnrolment,
  disableMfa,
  elevate,
  fetchMfaStatus,
  regenerateRecoveryCodes,
  type EnrolmentOffer,
} from './mfaApi';
import './mfa.css';

export function MfaScreen(): React.JSX.Element {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const [offer, setOffer] = useState<EnrolmentOffer | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);

  const status = useQuery({
    queryKey: ['mfa-status'],
    queryFn: fetchMfaStatus,
    gcTime: 0,
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['mfa-status'] });
  };

  const begin = useMutation({ mutationFn: beginEnrolment, onSuccess: setOffer });

  const error = [status.error, begin.error].find((e) => e instanceof Error) as ApiError | undefined;

  return (
    <div className="mfa">
      <header className="mfa__head">
        <div>
          <h1 className="mfa__title">{t('mfa.title')}</h1>
          <p className="mfa__lede">{t('mfa.lede')}</p>
        </div>
        <Link className="mfa__link" to="/liturgies">
          {t('mfa.your_liturgies')}
        </Link>
      </header>

      {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

      <div className="mfa__grid">
        <div className="mfa__main">
          {codes ? (
            <RecoveryCodes
              codes={codes}
              onDone={async () => {
                setCodes(null);
                setOffer(null);
                await refresh();
              }}
            />
          ) : offer ? (
            <Enrolment offer={offer} onConfirmed={setCodes} onCancel={() => setOffer(null)} />
          ) : status.isPending ? (
            <section className="mfa__card">
              <p className="mfa__fine">{t('mfa.loading')}</p>
            </section>
          ) : status.data?.enrolled ? (
            <Enrolled
              elevated={status.data.elevated}
              elevatedUntil={status.data.elevated_until}
              remaining={status.data.remaining_recovery_codes}
              onChanged={refresh}
              onNewCodes={setCodes}
            />
          ) : (
            <section className="mfa__card">
              <p className="mfa__eyebrow">{t('mfa.not_set_up')}</p>
              <p className="mfa__body">{t('mfa.not_set_up_body')}</p>
              <p className="mfa__fine">{t('mfa.not_set_up_fine')}</p>
              <div className="mfa__actions">
                <button
                  type="button"
                  className="mfa__btn"
                  disabled={begin.isPending}
                  onClick={() => begin.mutate()}
                >
                  {begin.isPending ? t('mfa.preparing') : t('mfa.setup_button')}
                </button>
              </div>
            </section>
          )}
        </div>

        <aside className="mfa__side">
          <section className="mfa__card mfa__card--warm">
            <p className="mfa__eyebrow">{t('mfa.protects_title')}</p>
            <p className="mfa__body">{t('mfa.protects_body')}</p>
            <p className="mfa__fine">{t('mfa.protects_fine')}</p>
          </section>

          <section className="mfa__card">
            <p className="mfa__eyebrow">{t('mfa.lasts_title')}</p>
            <p className="mfa__body">{t('mfa.lasts_body')}</p>
          </section>
        </aside>
      </div>
    </div>
  );
}

// ── enrolment ───────────────────────────────────────────────────────────────

function Enrolment({
  offer,
  onConfirmed,
  onCancel,
}: {
  offer: EnrolmentOffer;
  onConfirmed: (codes: string[]) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  const [code, setCode] = useState('');

  const confirm = useMutation({
    mutationFn: () => confirmEnrolment(code),
    onSuccess: (result) => onConfirmed(result.recovery_codes),
  });

  const error = confirm.error instanceof Error ? (confirm.error as ApiError) : undefined;

  return (
    <section className="mfa__card">
      <p className="mfa__eyebrow">{t('mfa.scan_eyebrow')}</p>

      <QrCode uri={offer.otpauth_uri} />

      <p className="mfa__fine">{t('mfa.manual_key_note')}</p>
      <p className="mfa__secret">{(offer.secret.match(/.{1,4}/g) ?? []).join(' ')}</p>

      <label className="mfa__field">
        <span className="mfa__field-label">{t('mfa.enter_code_label')}</span>
        <input
          className="mfa__code-input"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={7}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="000000"
        />
        <span className="mfa__fine">{t('mfa.enter_code_fine')}</span>
      </label>

      {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

      <div className="mfa__actions">
        <button
          type="button"
          className="mfa__btn"
          disabled={confirm.isPending || code.trim() === ''}
          onClick={() => confirm.mutate()}
        >
          {confirm.isPending ? t('mfa.checking') : t('mfa.confirm')}
        </button>
        <button type="button" className="mfa__ghost" onClick={onCancel}>
          {t('mfa.cancel')}
        </button>
      </div>
    </section>
  );
}

function QrCode({ uri }: { uri: string }): React.JSX.Element {
  const { t } = useTranslation('common');
  const canvas = useRef<HTMLCanvasElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!canvas.current) return;

    QRCode.toCanvas(canvas.current, uri, {
      width: 196,
      margin: 1,
      color: { dark: '#1A1A1A', light: '#FFFFFF' },
    }).catch(() => setFailed(true));
  }, [uri]);

  if (failed) {
    return <p className="mfa__fine">{t('mfa.qr_failed')}</p>;
  }

  return <canvas className="mfa__qr" ref={canvas} aria-label="Enrolment QR code" />;
}

// ── recovery codes ──────────────────────────────────────────────────────────

function RecoveryCodes({
  codes,
  onDone,
}: {
  codes: string[];
  onDone: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <section className="mfa__card mfa__card--warm">
      <p className="mfa__eyebrow">{t('mfa.save_now_eyebrow')}</p>
      <p className="mfa__body mfa__body--strong">{t('mfa.save_now_body')}</p>
      <p className="mfa__fine">{t('mfa.save_now_fine')}</p>

      <ul className="mfa__codes">
        {codes.map((code) => (
          <li key={code} className="mfa__recovery-code">
            {code}
          </li>
        ))}
      </ul>

      <label className="mfa__check">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
        />
        <span>{t('mfa.save_ack')}</span>
      </label>

      <div className="mfa__actions">
        <button type="button" className="mfa__btn" disabled={!acknowledged} onClick={onDone}>
          {t('mfa.done')}
        </button>
      </div>
    </section>
  );
}

// ── already enrolled ────────────────────────────────────────────────────────

function Enrolled({
  elevated,
  elevatedUntil,
  remaining,
  onChanged,
  onNewCodes,
}: {
  elevated: boolean;
  elevatedUntil: string | null;
  remaining: number;
  onChanged: () => Promise<void>;
  onNewCodes: (codes: string[]) => void;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  const [code, setCode] = useState('');
  const [removing, setRemoving] = useState(false);

  const confirm = useMutation({
    mutationFn: () => elevate(code),
    onSuccess: async () => {
      setCode('');
      await onChanged();
    },
  });

  const regenerate = useMutation({
    mutationFn: regenerateRecoveryCodes,
    onSuccess: (result) => onNewCodes(result.recovery_codes),
  });

  const remove = useMutation({
    mutationFn: () => disableMfa(code),
    onSuccess: async () => {
      setCode('');
      setRemoving(false);
      await onChanged();
    },
  });

  const error = [confirm.error, regenerate.error, remove.error].find((e) => e instanceof Error) as
    ApiError | undefined;

  return (
    <>
      <section className="mfa__card">
        <div className="mfa__card-head">
          <p className="mfa__eyebrow">{t('mfa.set_up_eyebrow')}</p>
          <span className={elevated ? 'mfa-pill mfa-pill--on' : 'mfa-pill'}>
            {elevated ? t('mfa.confirmed') : t('mfa.not_confirmed')}
          </span>
        </div>

        {elevated ? (
          <p className="mfa__body">
            {t('mfa.confirmed_body', {
              until: elevatedUntil ? ` until ${elevatedUntil.slice(11, 16)}` : '',
            })}
          </p>
        ) : (
          <>
            <p className="mfa__body">{t('mfa.not_confirmed_body')}</p>
            <label className="mfa__field">
              <span className="mfa__field-label">{t('mfa.code_label')}</span>
              <input
                className="mfa__code-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="000000"
              />
              <span className="mfa__fine">{t('mfa.recovery_code_hint')}</span>
            </label>
            <div className="mfa__actions">
              <button
                type="button"
                className="mfa__btn"
                disabled={confirm.isPending || code.trim() === ''}
                onClick={() => confirm.mutate()}
              >
                {confirm.isPending ? t('mfa.checking') : t('mfa.confirm')}
              </button>
            </div>
          </>
        )}
      </section>

      <section className="mfa__card">
        <div className="mfa__card-head">
          <p className="mfa__eyebrow">{t('mfa.recovery_codes_eyebrow')}</p>
          <span className={remaining <= 2 ? 'mfa-pill mfa-pill--low' : 'mfa-pill'}>
            {t('mfa.left_count', { count: remaining })}
          </span>
        </div>

        <p className="mfa__body">
          {remaining === 0
            ? t('mfa.none_left')
            : remaining <= 2
              ? t('mfa.nearly_gone')
              : t('mfa.each_signs_in')}
        </p>

        <div className="mfa__actions">
          <button
            type="button"
            className="mfa__btn mfa__btn--quiet"
            disabled={regenerate.isPending || !elevated}
            onClick={() => regenerate.mutate()}
          >
            {regenerate.isPending ? t('mfa.generating') : t('mfa.generate_set')}
          </button>
        </div>

        <p className="mfa__fine">
          {t('mfa.generate_fine')}
          {elevated ? '' : t('mfa.confirm_above_first')}
        </p>
      </section>

      <section className="mfa__card">
        <p className="mfa__eyebrow">{t('mfa.remove_eyebrow')}</p>

        {removing ? (
          <>
            <p className="mfa__body">{t('mfa.remove_prompt')}</p>
            <label className="mfa__field">
              <span className="mfa__field-label">{t('mfa.code_label')}</span>
              <input
                className="mfa__code-input"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="000000"
              />
              <span className="mfa__fine">{t('mfa.remove_required_fine')}</span>
            </label>
            <div className="mfa__actions">
              <button
                type="button"
                className="mfa__btn"
                disabled={remove.isPending || code.trim() === ''}
                onClick={() => remove.mutate()}
              >
                {remove.isPending ? t('mfa.removing') : t('mfa.remove_btn')}
              </button>
              <button type="button" className="mfa__ghost" onClick={() => setRemoving(false)}>
                {t('mfa.keep_btn')}
              </button>
            </div>
          </>
        ) : (
          <button type="button" className="mfa__ghost" onClick={() => setRemoving(true)}>
            {t('mfa.remove_action')}
          </button>
        )}
      </section>

      {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}
    </>
  );
}
